<template>
    <div class="rf-view">
        <NavigationBar :title="$t('settings.notifications.title')">
            <BackButton v-if="canPop" slot="left" @click="pop" />
        </NavigationBar>
        <main>
            <h1>{{ $t('settings.notifications.title') }}</h1>
            <p>{{ $t('settings.notifications.description') }}</p>

            <p v-if="didDeny" class="error-box">
                {{ $t('notifications.settings.permissionDeniedError') }}
            </p>

            <button v-if="didDeny" class="button destructive full" @click="openSettings">
                {{ $t('settings.notifications.settingsButton') }}
            </button>

            <template v-if="!didDeny">
                <h2>{{ $t('settings.notifications.typesTitle') }}</h2>
                <List>
                    <ListItem class="smartphone-wrap">
                        <span>{{ $t('settings.notifications.type.promotion') }}</span>
                        <div slot="right" class="segmented-control">
                            <button
                                v-for="opt in prefOptions"
                                :key="opt.value"
                                class="button"
                                :class="{ selected: preferences.promotion === opt.value }"
                                @click="setPreference('promotion', opt.value)"
                            >{{ $t(opt.labelKey) }}</button>
                        </div>
                    </ListItem>
                    <ListItem class="smartphone-wrap">
                        <span>{{ $t('settings.notifications.type.reminder') }}</span>
                        <div slot="right" class="segmented-control">
                            <button
                                v-for="opt in prefOptions"
                                :key="opt.value"
                                class="button"
                                :class="{ selected: preferences.reminder === opt.value }"
                                @click="setPreference('reminder', opt.value)"
                            >{{ $t(opt.labelKey) }}</button>
                        </div>
                    </ListItem>
                    <ListItem class="smartphone-wrap">
                        <span>{{ $t('settings.notifications.type.account') }}</span>
                        <div slot="right" class="segmented-control">
                            <button
                                v-for="opt in prefOptions"
                                :key="opt.value"
                                class="button"
                                :class="{ selected: preferences.account === opt.value }"
                                @click="setPreference('account', opt.value)"
                            >{{ $t(opt.labelKey) }}</button>
                        </div>
                    </ListItem>
                </List>
            </template>
        </main>
    </div>
</template>

<script lang="ts">
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { BackButton, List, ListItem, NavigationBar } from "@restofrit/components";
import { Session } from "@restofrit/networking";
import { Customer, CustomerNotificationPreferences, CustomerSettings, NotificationPreference } from "@restofrit/structures";
import { Decoder } from "@simonbackx/simple-encoding";
import { Request } from "@simonbackx/simple-networking";
import { NavigationMixin } from "@simonbackx/vue-app-navigation";
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { Component, Mixins, Prop } from "vue-property-decorator";

@Component({
    components: {
        NavigationBar,
        List,
        ListItem,
        BackButton
    },
})
export default class NotificationSettingsView extends Mixins(NavigationMixin) {
    @Prop({ required: true })
    customer!: Customer

    didDeny = false

    prefOptions = [
        { value: NotificationPreference.Off, labelKey: 'settings.notifications.pref.off' },
        { value: NotificationPreference.Loud, labelKey: 'settings.notifications.pref.loud' },
    ]

    get preferences(): CustomerNotificationPreferences {
        return this.customer.settings.notificationPreferences
            ?? CustomerNotificationPreferences.create({})
    }

    mounted() {
        this.handleVisibilityChange()
        document.addEventListener("visibilitychange", this.handleVisibilityChange, { passive: true });
    }

    handleVisibilityChange() {
        PushNotifications.checkPermissions().then(permissions => {
            this.didDeny = permissions.receive === 'denied'
            if (permissions.receive === 'granted') {
                PushNotifications.register().catch(console.error)
            }
        }).catch(console.error);
    }

    beforeDestroy() {
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    }

    setPreference(type: 'promotion' | 'reminder' | 'account', value: NotificationPreference) {
        const current = this.preferences
        const updated = CustomerNotificationPreferences.create({
            promotion: current.promotion,
            reminder: current.reminder,
            account: current.account,
            [type]: value
        })

        Request.cancelAll(this)
        Session.shared.authenticatedServer.request({
            method: "PATCH",
            path: "/customers/" + this.customer.id,
            body: CustomerSettings.patch({
                notificationPreferences: updated
            }),
            decoder: Customer as Decoder<Customer>,
            owner: this
        }).then(response => {
            this.customer.set(response.data)
        }).catch(() => {
            console.error("Failed to save notification preferences")
        })
    }

    openSettings() {
        if (Capacitor.getPlatform() === 'ios') {
            NativeSettings.openIOS({ option: IOSSettings.App });
        } else {
            NativeSettings.openAndroid({ option: AndroidSettings.ApplicationDetails });
        }
    }
}
</script>

<style lang="scss">
@use '~@restofrit/scss/base/variables' as *;

.segmented-control {
    display: flex;
    background: $color-background-gray;
    border-radius: $border-radius;
    padding: 3px;
    gap: 2px;

    > .button {
        flex: 1;
        padding: 7px 12px;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.2;
        color: $color-gray;
        border-radius: 4px;
        text-align: center;
        white-space: nowrap;
        transition: background-color 0.2s, color 0.2s;

        &.selected {
            background: $color-primary;
            color: $color-white;
        }
    }
}
</style>
